import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useProjects } from '../hooks/useProjects';
import { useTasks } from '../hooks/useTasks';
import { useLanguage } from '../contexts/LanguageContext';
import { Project } from '../types/project';
import { Task, CreateTaskData } from '../types/task';
import { db } from '../lib/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { actorStamp } from '../lib/audit';
import LoadingScreen from '../components/common/LoadingScreen';
import ProjectForm from '../components/projects/ProjectForm';
import TaskListView from '../components/tasks/views/TaskListView';
import ConfirmDialog from '../components/common/ConfirmDialog';
import ProjectGanttView from '../components/projects/ProjectGanttView';
import ProjectBacklogView from '../components/projects/ProjectBacklogView';
import ProjectCalendarView from '../components/projects/ProjectCalendarView';
import TaskForm from '../components/tasks/TaskForm';
import TaskView from '../components/tasks/TaskView';
import TaskBoard from '../components/tasks/views/TaskBoard';
import { PROJECT_PRIORITY_META, ProjectStatusSelect, ProjectAvatars, fmtDate, isProjectOverdue, daysLeft, toDate } from '../components/projects/projectMeta';
import { toDate as tToDate } from '../components/tasks/views/taskMeta';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon, PencilSquareIcon, PlusIcon, CalendarIcon, BuildingOfficeIcon, UserIcon, DocumentTextIcon,
  ArrowTopRightOnSquareIcon, ExclamationTriangleIcon, Squares2X2Icon, ListBulletIcon, ChartBarIcon, QueueListIcon, CalendarDaysIcon, ViewColumnsIcon, TagIcon, BanknotesIcon
} from '@heroicons/react/24/outline';

type WsTab = 'overview' | 'board' | 'list' | 'gantt' | 'backlog' | 'calendar';
const TABS: { id: WsTab; label: string; icon: React.ElementType }[] = [
  { id: 'overview', label: 'Áttekintés', icon: Squares2X2Icon },
  { id: 'board', label: 'Tábla', icon: ViewColumnsIcon },
  { id: 'list', label: 'Lista', icon: ListBulletIcon },
  { id: 'gantt', label: 'Gantt', icon: ChartBarIcon },
  { id: 'backlog', label: 'Backlog / Sprintek', icon: QueueListIcon },
  { id: 'calendar', label: 'Naptár', icon: CalendarDaysIcon }
];

const fmtMoney = (n?: number, c = 'HUF') => (typeof n === 'number' ? new Intl.NumberFormat('hu-HU', { style: 'currency', currency: c, maximumFractionDigits: 0 }).format(n) : '–');

const ProjectWorkspace: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { projects, loading, updateProject, updateProjectStatus } = useProjects();
  const { tasks, createTask, updateTask, updateTaskStatus, updateTaskPriority, assignTask, unassignTask, deleteTask, addComment, deleteComment, updateComment, deleteCommentAttachment, downloadTasksAsText } = useTasks();

  const project = useMemo(() => projects.find(p => p.id === projectId), [projects, projectId]);
  const [tab, setTabState] = useState<WsTab>(() => {
    try { const v = localStorage.getItem(`project.${projectId}.tab`); if (v) return v as WsTab; } catch { /* ignore */ }
    return 'overview';
  });
  const setTab = (v: WsTab) => { setTabState(v); try { localStorage.setItem(`project.${projectId}.tab`, v); } catch { /* ignore */ } };
  const [editOpen, setEditOpen] = useState(false);
  const [taskFormOpen, setTaskFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | undefined>(undefined);
  const [viewingTask, setViewingTask] = useState<Task | undefined>(undefined);
  const [deletingTaskId, setDeletingTaskId] = useState<string | null>(null);

  useEffect(() => {
    if (viewingTask) { const u = tasks.find(x => x.id === viewingTask.id); if (u) setViewingTask(u); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  const projectTasks = useMemo(() => tasks.filter(tk => tk.projectId === projectId), [tasks, projectId]);
  const stats = useMemo(() => {
    const live = projectTasks.filter(tk => tk.status !== 'cancelled');
    const done = live.filter(tk => tk.status === 'completed').length;
    const inProgress = live.filter(tk => tk.status === 'in_progress').length;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const overdue = live.filter(tk => tk.status !== 'completed' && tToDate(tk.dueDate) && (tToDate(tk.dueDate) as Date) < today).length;
    const hours = live.reduce((s, tk) => s + (tk.actualHours || 0), 0);
    const est = live.reduce((s, tk) => s + (tk.estimatedHours || 0), 0);
    return { total: live.length, done, inProgress, overdue, hours, est, progress: live.length ? Math.round((done / live.length) * 100) : 0 };
  }, [projectTasks]);

  if (loading) return <LoadingScreen />;
  if (!project) {
    return (
      <div className="p-6">
        <div className="crm-card p-8 text-center">
          <div className="text-sm text-gray-500 dark:text-gray-400">A projekt nem található.</div>
          <button type="button" onClick={() => navigate('/projects')} className="crm-btn crm-btn-secondary mt-3"><ArrowLeftIcon className="w-4 h-4 mr-1.5" />Vissza a projektekhez</button>
        </div>
      </div>
    );
  }

  const pr = PROJECT_PRIORITY_META[project.priority] || PROJECT_PRIORITY_META.medium;
  const overdue = isProjectOverdue(project);
  const left = daysLeft(project);
  const color = project.color || '#0f7f75';

  const handleCreateTask = async (data: CreateTaskData) => {
    try {
      await createTask({ ...data, projectId: project.id, customerId: data.customerId || project.customerId });
      setTaskFormOpen(false);
      toast.success('Feladat létrehozva');
    } catch (e) { console.error(e); toast.error('A feladat létrehozása nem sikerült'); }
  };
  const handleUpdateTask = async (data: CreateTaskData) => {
    if (!editingTask) return;
    try {
      await updateTask(editingTask.id, data);
      setTaskFormOpen(false); setEditingTask(undefined);
      toast.success('Feladat frissítve');
    } catch (e) { console.error(e); toast.error('A feladat mentése nem sikerült'); }
  };

  const linked = [
    { label: 'Árajánlat', n: project.quotationIds?.length || 0, to: '/quotations' },
    { label: 'Megrendelés', n: project.orderIds?.length || 0, to: '/orders' },
    { label: 'Szállítólevél', n: project.deliveryNoteIds?.length || 0, to: '/delivery-notes' },
    { label: 'Számla', n: project.invoiceIds?.length || 0, to: '/invoices' }
  ].filter(x => x.n > 0);

  return (
    <div className="space-y-4">
      {/* Fejléc */}
      <div className="crm-card overflow-hidden">
        <div className="h-1" style={{ backgroundColor: color }} />
        <div className="p-4 sm:p-5">
          <button type="button" onClick={() => navigate('/projects')} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white mb-2"><ArrowLeftIcon className="w-3.5 h-3.5" />Projektek</button>
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center text-lg flex-shrink-0" style={{ backgroundColor: `${color}1a`, color }}>{project.icon || (project.key || project.name).slice(0, 2).toUpperCase()}</div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-50 break-words">{project.name}</h1>
                  <span className="text-xs font-mono text-gray-400">{project.key}</span>
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-gray-500 dark:text-gray-400">
                  <ProjectStatusSelect value={project.status} onChange={async (s) => { try { await updateProjectStatus(project.id, s); } catch { toast.error('Státusz mentése sikertelen'); } }} />
                  <span className={`inline-flex items-center gap-1 ${pr.text}`}><span className={`w-1.5 h-1.5 rounded-full ${pr.dot}`} />{pr.label}</span>
                  <span className={`inline-flex items-center gap-1 tabular-nums ${overdue ? 'text-rose-600 dark:text-rose-400' : ''}`}><CalendarIcon className="w-3.5 h-3.5" />{fmtDate(project.startDate)} – {fmtDate(project.endDate)}{overdue && <ExclamationTriangleIcon className="w-3.5 h-3.5" />}{!overdue && left !== null && project.status === 'active' && <span className="opacity-80">· {left >= 0 ? `${left} nap van hátra` : ''}</span>}</span>
                  {project.customerName && <span className="inline-flex items-center gap-1"><BuildingOfficeIcon className="w-3.5 h-3.5" />{project.customerName}</span>}
                  {project.teamLeadName && <span className="inline-flex items-center gap-1"><UserIcon className="w-3.5 h-3.5" />{project.teamLeadName}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <ProjectAvatars names={project.assignedUsersNames} max={5} />
              <button type="button" onClick={() => setEditOpen(true)} className="crm-btn crm-btn-secondary"><PencilSquareIcon className="w-4 h-4 mr-1.5" />Szerkesztés</button>
              <button type="button" onClick={() => { setEditingTask(undefined); setTaskFormOpen(true); }} className="crm-btn crm-btn-primary"><PlusIcon className="w-4 h-4 mr-1.5" />Új feladat</button>
            </div>
          </div>
          {/* haladás */}
          <div className="mt-4 flex items-center gap-3">
            <div className="flex-1 h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden"><div className={`h-full rounded-full ${stats.progress >= 100 ? 'bg-emerald-500' : ''}`} style={{ width: `${stats.progress}%`, backgroundColor: stats.progress >= 100 ? undefined : color }} /></div>
            <span className="text-xs tabular-nums text-gray-600 dark:text-gray-300 whitespace-nowrap">{stats.done}/{stats.total} feladat · {stats.progress}%</span>
          </div>
        </div>
        {/* fülek */}
        <div className="px-2 sm:px-4 border-t border-[var(--crm-border)] flex gap-1 overflow-x-auto [scrollbar-width:none]">
          {TABS.map(tb => (
            <button key={tb.id} type="button" onClick={() => setTab(tb.id)} className={`inline-flex items-center gap-1.5 h-10 px-3 text-[13px] font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === tb.id ? 'border-primary-600 text-primary-700 dark:text-primary-300' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'}`}>
              <tb.icon className="w-4 h-4" />{tb.label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-4">
          <div className="space-y-4 min-w-0">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { label: 'Nyitott', n: stats.total - stats.done, dot: 'bg-gray-400' },
                { label: 'Folyamatban', n: stats.inProgress, dot: 'bg-sky-500' },
                { label: 'Kész', n: stats.done, dot: 'bg-emerald-500' },
                { label: 'Lejárt', n: stats.overdue, dot: 'bg-rose-500' }
              ].map(k => (
                <div key={k.label} className="crm-card px-3 py-2">
                  <div className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400"><span className={`w-1.5 h-1.5 rounded-full ${k.dot}`} />{k.label}</div>
                  <div className="text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-50 leading-tight">{k.n}</div>
                </div>
              ))}
            </div>

            <section className="crm-card p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Leírás</h3>
              {project.description ? <div className="prose prose-sm dark:prose-invert max-w-none text-gray-800 dark:text-gray-200" dangerouslySetInnerHTML={{ __html: project.description }} /> : <p className="text-sm text-gray-400">Nincs leírás.</p>}
              {project.notes && <div className="mt-3 pt-3 border-t border-[var(--crm-border)] text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap"><span className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold block mb-1">Jegyzet</span>{project.notes}</div>}
            </section>

            <section className="crm-card p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Feladatok táblája</h3>
                <button type="button" onClick={() => setTab('board')} className="text-xs text-primary-600 hover:underline">Teljes tábla →</button>
              </div>
              <TaskBoard tasks={projectTasks} onUpdateStatus={(id, s, sid) => updateTaskStatus(id, s, sid)} onView={(tk) => setViewingTask(tk)} onNew={() => { setEditingTask(undefined); setTaskFormOpen(true); }} />
            </section>
          </div>

          <aside className="space-y-4">
            <section className="crm-card p-4 space-y-3 text-sm">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Adatok</h3>
              <div className="flex justify-between gap-2"><span className="text-gray-500">Csapat</span><ProjectAvatars names={project.assignedUsersNames} max={6} /></div>
              <div className="flex justify-between gap-2"><span className="text-gray-500">Vezető</span><span>{project.teamLeadName || '–'}</span></div>
              <div className="flex justify-between gap-2"><span className="text-gray-500">Ügyfél</span><span className="truncate">{project.customerName || '–'}</span></div>
              <div className="flex justify-between gap-2"><span className="text-gray-500">Láthatóság</span><span>{project.visibility === 'private' ? 'Privát' : project.visibility === 'team' ? 'Csapat' : 'Nyilvános'}</span></div>
              <div className="flex justify-between gap-2"><span className="text-gray-500">Órák</span><span className="tabular-nums">{stats.hours.toFixed(1)} ó{stats.est ? ` / ${stats.est} ó` : ''}</span></div>
              {(project.budget || project.actualCost) ? (
                <div>
                  <div className="flex justify-between gap-2"><span className="text-gray-500 inline-flex items-center gap-1"><BanknotesIcon className="w-3.5 h-3.5" />Költségvetés</span><span className="tabular-nums">{fmtMoney(project.actualCost || 0, project.currency)} / {fmtMoney(project.budget, project.currency)}</span></div>
                  {project.budget ? <div className="mt-1 h-1 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden"><div className={`h-full rounded-full ${(project.actualCost || 0) > project.budget ? 'bg-rose-500' : 'bg-primary-600'}`} style={{ width: `${Math.min(100, Math.round(((project.actualCost || 0) / project.budget) * 100))}%` }} /></div> : null}
                </div>
              ) : null}
              {project.tags?.length > 0 && <div className="flex flex-wrap gap-1 pt-1">{project.tags.map(tg => <span key={tg} className="inline-flex items-center gap-1 px-2 h-6 rounded-full bg-gray-100 dark:bg-gray-700 text-xs"><TagIcon className="w-3 h-3" />{tg}</span>)}</div>}
            </section>

            {linked.length > 0 && (
              <section className="crm-card p-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Kapcsolt dokumentumok</h3>
                <ul className="space-y-1 text-sm">{linked.map(l => <li key={l.label} className="flex justify-between"><span>{l.label}</span><button type="button" onClick={() => navigate(l.to)} className="text-primary-600 hover:underline tabular-nums">{l.n} db →</button></li>)}</ul>
              </section>
            )}

            {project.documents?.length > 0 && (
              <section className="crm-card p-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Dokumentumok ({project.documents.length})</h3>
                <ul className="space-y-1.5 text-sm">
                  {project.documents.map((d, i) => (
                    <li key={d.id || i}>
                      <a
                        href={d.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={d.originalName || d.name}
                        title={`${d.originalName || d.name}${d.size ? ` · ${Math.round(d.size / 1024)} KB` : ''} – megnyitás / letöltés`}
                        className="group flex items-center justify-between gap-2 -mx-1.5 px-1.5 py-1 rounded-md hover:bg-gray-100 dark:hover:bg-white/5"
                      >
                        <span className="inline-flex items-center gap-2 min-w-0">
                          <DocumentTextIcon className="w-4 h-4 text-gray-400 group-hover:text-primary-600 flex-shrink-0" />
                          <span className="truncate text-gray-800 dark:text-gray-100 group-hover:text-primary-700 dark:group-hover:text-primary-300">{d.originalName || d.name}</span>
                        </span>
                        <span className="inline-flex items-center gap-1 text-[11px] text-gray-400 flex-shrink-0">
                          {d.size ? `${Math.round(d.size / 1024)} KB` : ''}
                          <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <div className="text-[11px] text-gray-400 dark:text-gray-500 px-1">Létrehozta: {project.createdByName || '–'} · {fmtDate(project.createdAt)}{toDate(project.updatedAt) ? ` · Módosítva: ${fmtDate(project.updatedAt)}` : ''}</div>
          </aside>
        </div>
      )}

      {tab !== 'overview' && (
        <div className="crm-card p-3 sm:p-4">
          {tab === 'board' && (
            <TaskBoard tasks={projectTasks} onUpdateStatus={(id, s, sid) => updateTaskStatus(id, s, sid)} onView={(tk) => setViewingTask(tk)} onNew={() => { setEditingTask(undefined); setTaskFormOpen(true); }} />
          )}
          {tab === 'list' && (
            <TaskListView
              tasks={projectTasks}
              onView={(tk) => setViewingTask(tk)}
              onEdit={(tk) => { setEditingTask(tk); setTaskFormOpen(true); }}
              onDelete={(id) => setDeletingTaskId(id)}
              onUpdateStatus={(id, s, sid) => updateTaskStatus(id, s, sid).catch(() => toast.error('A státusz mentése nem sikerült'))}
              onUpdatePriority={(id, pr2) => updateTaskPriority(id, pr2).catch(() => toast.error('A prioritás mentése nem sikerült'))}
              onAssign={(id, uid, name) => assignTask(id, uid, name).catch(() => toast.error('A felelős mentése nem sikerült'))}
              onUnassign={(id) => unassignTask(id).catch(() => toast.error('A felelős eltávolítása nem sikerült'))}
              onUpdateDueDate={async (id, d) => { try { await updateDoc(doc(db, 'tasks', id), { dueDate: d || null, ...actorStamp() }); } catch { toast.error('A határidő mentése nem sikerült'); } }}
              onUpdateSubtasks={async (id, subtasks) => { try { await updateTask(id, { subtasks }); } catch { toast.error('Az alfeladatok mentése nem sikerült'); } }}
              onQuickCreate={async (title, status, statusId) => {
                try { await createTask({ title, status, statusId, priority: 'medium', tags: [], documents: [], projectId: project.id, customerId: project.customerId } as CreateTaskData); }
                catch (e) { console.error(e); toast.error('A feladat létrehozása nem sikerült'); }
              }}
              onBulkDelete={async (ids) => { try { for (const id of ids) await deleteTask(id); toast.success(`${ids.length} feladat törölve`); } catch { toast.error('A törlés nem sikerült'); } }}
            />
          )}
          {tab === 'gantt' && <ProjectGanttView project={project} />}
          {tab === 'backlog' && <ProjectBacklogView project={project} />}
          {tab === 'calendar' && <ProjectCalendarView project={project} />}
        </div>
      )}

      <ProjectForm
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        onSubmit={async (data: any) => { try { await updateProject(project.id, data); setEditOpen(false); toast.success('Projekt frissítve'); } catch (e) { console.error(e); toast.error('A projekt mentése nem sikerült'); } }}
        initialData={project}
      />

      {taskFormOpen && (
        <TaskForm
          isOpen={taskFormOpen}
          onClose={() => { setTaskFormOpen(false); setEditingTask(undefined); }}
          onSubmit={editingTask ? handleUpdateTask : handleCreateTask}
          initialData={editingTask}
        />
      )}

      <ConfirmDialog
        isOpen={!!deletingTaskId}
        title="Feladat törlése"
        message="Biztosan törlöd ezt a feladatot? A művelet nem vonható vissza."
        confirmText="Törlés"
        cancelText="Mégse"
        type="danger"
        onConfirm={async () => { if (!deletingTaskId) return; try { await deleteTask(deletingTaskId); toast.success('Feladat törölve'); } catch { toast.error('A törlés nem sikerült'); } setDeletingTaskId(null); }}
        onClose={() => setDeletingTaskId(null)}
      />
      {viewingTask && (
        <TaskView
          task={viewingTask}
          isOpen
          onClose={() => setViewingTask(undefined)}
          onEdit={(tk) => { setEditingTask(tk); setTaskFormOpen(true); }}
          onAddComment={(m, f) => addComment(viewingTask.id, m, f)}
          onDeleteComment={(cid) => deleteComment(viewingTask.id, cid)}
          onUpdateComment={(cid, m) => updateComment(viewingTask.id, cid, m)}
          onDeleteAttachment={(cid, aid) => deleteCommentAttachment(viewingTask.id, cid, aid)}
          onExportTask={() => downloadTasksAsText([viewingTask], `${viewingTask.taskNumber}_${viewingTask.title}.txt`)}
          onUpdateStatus={(id, s, sid) => updateTaskStatus(id, s, sid)}
          onUpdateSubtasks={async (id, subtasks) => { try { await updateTask(id, { subtasks }); } catch { toast.error('Az alfeladatok mentése nem sikerült'); } }}
        />
      )}
    </div>
  );
};

export default ProjectWorkspace;
